import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, UserPlus, AlertCircle, CheckCircle } from 'lucide-react';
import { api } from '../../lib/api';

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [invitation, setInvitation] = useState<{ email: string; role: string } | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await api.get<{ email: string; role: string }>(
          `/api/users/invite/${token}`
        );
        setInvitation(response);
      } catch (err: any) {
        setError(err.message || '無効な招待リンクです');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      validateToken();
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    if (password.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      return;
    }

    setSubmitting(true);

    try {
      await api.post(`/api/users/invite/${token}/accept`, {
        name,
        password,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'アカウント作成に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-fadeIn">
        <div
          className="card p-8 text-center"
          style={{
            backgroundColor: 'var(--background-card)',
            borderColor: 'var(--border-color)',
          }}
        >
          <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>
            招待リンクを確認中...
          </p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="animate-fadeIn">
        <div
          className="card p-8 text-center"
          style={{
            backgroundColor: 'var(--background-card)',
            borderColor: 'var(--border-color)',
          }}
        >
          <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-red-600" size={32} />
          </div>
          <h2
            className="text-xl font-bold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            招待リンクが無効です
          </h2>
          <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
            {error}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white font-medium"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            ログイン画面へ
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="animate-fadeIn">
        <div
          className="card p-8 text-center"
          style={{
            backgroundColor: 'var(--background-card)',
            borderColor: 'var(--border-color)',
          }}
        >
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="text-green-600" size={32} />
          </div>
          <h2
            className="text-xl font-bold mb-2"
            style={{ color: 'var(--text-primary)' }}
          >
            アカウントが作成されました
          </h2>
          <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
            ログイン画面からログインしてください。
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-white font-medium"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            ログイン画面へ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div
        className="card p-8"
        style={{
          backgroundColor: 'var(--background-card)',
          borderColor: 'var(--border-color)',
        }}
      >
        <h2
          className="text-2xl font-bold mb-2"
          style={{ color: 'var(--text-primary)' }}
        >
          アカウント作成
        </h2>
        <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
          <strong>{invitation?.email}</strong> として招待されています。
          <br />
          名前とパスワードを設定してください。
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center gap-2 text-red-600">
            <AlertCircle size={18} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--text-primary)' }}
            >
              お名前
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border transition-colors"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
              placeholder="山田 太郎"
              required
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--text-primary)' }}
            >
              パスワード
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 pr-10 rounded-lg border transition-colors"
                style={{
                  backgroundColor: 'var(--input-bg)',
                  borderColor: 'var(--input-border)',
                  color: 'var(--text-primary)',
                }}
                placeholder="6文字以上"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-muted)' }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-1.5"
              style={{ color: 'var(--text-primary)' }}
            >
              パスワード（確認）
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border transition-colors"
              style={{
                backgroundColor: 'var(--input-bg)',
                borderColor: 'var(--input-border)',
                color: 'var(--text-primary)',
              }}
              placeholder="パスワードを再入力"
              required
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white font-medium transition-colors disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            {submitting ? (
              <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <UserPlus size={18} />
                アカウントを作成
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

